import React from "react";

/*
 * OSIcon32 — 32px program icons for the NeighborhoodOS desktop, taskbar and
 * window title bars.
 *
 * Companion to the existing 24px icon components, NOT a replacement:
 *   DockIcon.jsx          — 24x24 app glyphs (keep for in-window use)
 *   BadgeIcon.jsx         — 24x24 trophy-case badges
 *   ResourceIcon.jsx      — 24x24 resource pictograms
 *   ReviewRatingIcon.jsx  — 24x24 review marks
 *
 * Provenance of each glyph below:
 *   navigator / pitch / messenger / map / portal / resources
 *       redrawn on the 32 grid from DockIcon's website / submit /
 *       buddyMessenger / founderMap / lpPortal / resources glyphs.
 *   trophy   — BadgeIcon's TrophyCup primitive, re-seated on the 32 grid.
 *   chapters — BadgeIcon's welcome_neighborhood house cluster, ditto.
 *   favorite / consider / pass / ineligible / norating
 *       ReviewRatingIcon's Favorite / Consideration / Pass / Ineligible /
 *       "No Rating" glyphs, unchanged shapes on a 24 viewBox.
 *   folder / sheet / note / donate / computer — new, drawn to the same recipe.
 *
 * DRAWING RECIPE (all 32-grid glyphs follow it, so icons and window bevels
 * read as one system):
 *   1. a hard offset shadow silhouette, +1.5x +1.5y, fill --chrome-outline
 *   2. the shape itself, 1px --chrome-outline stroke, flat brand fill
 *   3. a single 1px top-left highlight in the fill's light tint
 * No blur, no gradients, no anti-aliased curves where a step will do:
 * shape-rendering="crispEdges" on every 32-grid glyph.
 */

const INK = "#141419";
const WHITE = "#ffffff";

const FILLS = {
  magenta: "#e93a7d",
  magentaDeep: "#c21d61",
  magentaSoft: "#fde0ec",
  grape: "#6b4fbb",
  grapeDeep: "#4a2f95",
  grapeSoft: "#e7dffa",
  grapeMid: "#b9a6ea",
  aqua: "#2bb3c4",
  aquaDeep: "#157b8a",
  aquaSoft: "#d5f1f4",
  aquaMid: "#7fd4de",
  tangerine: "#f28c3b",
  tangerineDeep: "#c66915",
  tangerineSoft: "#fde7d0",
  butter: "#f0c94b",
  butterDeep: "#c89918",
  butterSoft: "#fbf1cc",
  paper: "#faf4e3",
  paperDeep: "#ede0bd",
  /* Chrome greys match the pink-tinted ramp in win95-tokens.css */
  silver: "#cfc2c9",
  silverFace: "#d6ccd2",
  shade: "#9c8c95",
  rose: "#e94560",
  greenLcd: "#90ee90",
  greenMid: "#b3e6cc",
  greenDark: "#5ba87d",
  slate: "#6b7280",
  rule: "#b9b6ae",
};

/* ── 32-grid program icons ─────────────────────────────────────────────── */

const GLYPHS_32 = {
  navigator: (
    <g>
      <rect x="3" y="4" width="27" height="25" fill={INK} />
      <rect x="2" y="3" width="27" height="25" fill={FILLS.silverFace} stroke={INK} strokeWidth="1" />
      <rect x="3" y="4" width="25" height="5" fill={FILLS.magentaDeep} />
      <rect x="3" y="4" width="25" height="1" fill="#f6b3cd" />
      <rect x="5" y="6" width="2" height="2" fill="#ffeaf5" />
      <rect x="8" y="6" width="2" height="2" fill="#ffeaf5" />
      <rect x="11" y="6" width="2" height="2" fill="#ffeaf5" />
      <rect x="4" y="10" width="23" height="17" fill="#e8f0ff" />
      <circle cx="15" cy="18" r="7" fill={FILLS.aqua} stroke={INK} strokeWidth="1" />
      <path d="M 8 18 h 14 M 15 11 v 14" stroke={FILLS.aquaDeep} strokeWidth="1" fill="none" />
      <path d="M 15 11 C 11 14 11 22 15 25 C 19 22 19 14 15 11 Z" fill="none" stroke={FILLS.aquaDeep} strokeWidth="1" />
      <rect x="9" y="13" width="4" height="1" fill={FILLS.aquaSoft} />
      <rect x="21" y="20" width="6" height="6" fill={FILLS.magenta} stroke={INK} strokeWidth="1" />
      <rect x="23" y="22" width="2" height="2" fill={WHITE} />
    </g>
  ),

  pitch: (
    <g>
      <rect x="7" y="4" width="18" height="26" fill={INK} />
      <rect x="6" y="3" width="18" height="26" fill={WHITE} stroke={INK} strokeWidth="1" />
      <rect x="7" y="4" width="16" height="1" fill={FILLS.paper} />
      <rect x="9" y="6" width="12" height="3" fill={FILLS.magenta} />
      <rect x="9" y="12" width="12" height="1" fill={FILLS.slate} />
      <rect x="9" y="15" width="12" height="1" fill={FILLS.slate} />
      <rect x="9" y="18" width="8" height="1" fill={FILLS.slate} />
      <rect x="9" y="21" width="10" height="1" fill={FILLS.slate} />
      <g transform="rotate(38 22 20)">
        <rect x="20" y="10" width="5" height="14" fill={FILLS.butter} stroke={INK} strokeWidth="1" />
        <rect x="21" y="11" width="1" height="12" fill={FILLS.butterSoft} />
        <rect x="20" y="8" width="5" height="3" fill={FILLS.magenta} stroke={INK} strokeWidth="1" />
        <polygon points="20,24 25,24 22.5,29" fill={FILLS.paper} stroke={INK} strokeWidth="1" />
        <rect x="21" y="27" width="3" height="2" fill={INK} />
      </g>
    </g>
  ),

  messenger: (
    <g>
      <rect x="4" y="5" width="19" height="14" fill={INK} />
      <rect x="3" y="4" width="19" height="14" fill={FILLS.grape} stroke={INK} strokeWidth="1" />
      <rect x="4" y="5" width="17" height="1" fill={FILLS.grapeMid} />
      <polygon points="7,18 6,23 12,18" fill={FILLS.grape} stroke={INK} strokeWidth="1" />
      <rect x="7" y="9" width="11" height="1" fill={FILLS.grapeSoft} />
      <rect x="7" y="12" width="8" height="1" fill={FILLS.grapeSoft} />
      <rect x="14" y="15" width="15" height="13" fill={INK} />
      <rect x="13" y="14" width="15" height="13" fill={FILLS.aqua} stroke={INK} strokeWidth="1" />
      <rect x="14" y="15" width="13" height="1" fill={FILLS.aquaSoft} />
      <polygon points="24,27 25,31 20,27" fill={FILLS.aqua} stroke={INK} strokeWidth="1" />
      <rect x="16" y="19" width="2" height="2" fill={FILLS.butter} />
      <rect x="20" y="19" width="2" height="2" fill={FILLS.butter} />
      <rect x="24" y="19" width="2" height="2" fill={FILLS.butter} />
    </g>
  ),

  map: (
    <g>
      <polygon points="4,8 12,6 21,9 29,7 29,26 21,28 12,25 4,27" fill={INK} />
      <polygon points="3,7 11,5 20,8 28,6 28,25 20,27 11,24 3,26" fill={FILLS.tangerine} stroke={INK} strokeWidth="1" />
      <polygon points="3,7 11,5 11,24 3,26" fill="#f6c393" />
      <path d="M 11 5 v 19 M 20 8 v 19" stroke={FILLS.tangerineDeep} strokeWidth="1" strokeDasharray="2,2" fill="none" />
      <path d="M 5 16 C 9 14 14 20 19 17 C 23 15 26 18 27 17" stroke={FILLS.aquaDeep} strokeWidth="1.5" fill="none" />
      <path d="M 16 9 C 19 9 21 11 21 14 C 21 18 16 24 16 24 C 16 24 11 18 11 14 C 11 11 13 9 16 9 Z" fill={FILLS.magenta} stroke={INK} strokeWidth="1" />
      <circle cx="16" cy="14" r="2" fill={WHITE} stroke={INK} strokeWidth="1" />
    </g>
  ),

  portal: (
    <g>
      <path d="M 11 10 V 7 C 11 5 13 4 16 4 C 19 4 21 5 21 7 V 10" fill="none" stroke={INK} strokeWidth="2" />
      <rect x="4" y="11" width="26" height="18" fill={INK} />
      <rect x="3" y="10" width="26" height="18" fill={FILLS.grape} stroke={INK} strokeWidth="1" />
      <rect x="4" y="11" width="24" height="1" fill={FILLS.grapeMid} />
      <rect x="3" y="14" width="26" height="4" fill={FILLS.grapeDeep} stroke={INK} strokeWidth="1" />
      <rect x="13" y="13" width="6" height="6" fill={FILLS.butter} stroke={INK} strokeWidth="1" />
      <rect x="15" y="15" width="2" height="2" fill={INK} />
      <rect x="8" y="23" width="4" height="4" fill={FILLS.butter} stroke={INK} strokeWidth="1" />
      <rect x="14" y="21" width="4" height="6" fill={FILLS.butter} stroke={INK} strokeWidth="1" />
      <rect x="20" y="19" width="4" height="8" fill={FILLS.butter} stroke={INK} strokeWidth="1" />
    </g>
  ),

  resources: (
    <g>
      <rect x="5" y="23" width="24" height="6" fill={INK} />
      <rect x="4" y="22" width="24" height="6" fill={FILLS.aqua} stroke={INK} strokeWidth="1" />
      <rect x="5" y="23" width="22" height="1" fill={FILLS.aquaSoft} />
      <rect x="6" y="17" width="22" height="6" fill={INK} />
      <rect x="5" y="16" width="22" height="6" fill={FILLS.tangerine} stroke={INK} strokeWidth="1" />
      <rect x="6" y="17" width="20" height="1" fill={FILLS.tangerineSoft} />
      <rect x="7" y="7" width="20" height="10" fill={INK} />
      <rect x="6" y="6" width="20" height="10" fill={FILLS.paper} stroke={INK} strokeWidth="1" />
      <rect x="6" y="6" width="4" height="10" fill={FILLS.magenta} stroke={INK} strokeWidth="1" />
      <rect x="12" y="9" width="11" height="1" fill={FILLS.slate} />
      <rect x="12" y="12" width="8" height="1" fill={FILLS.slate} />
    </g>
  ),

  folder: (
    <g>
      <polygon points="4,9 14,9 17,12 29,12 29,27 4,27" fill={INK} />
      <polygon points="3,8 13,8 16,11 28,11 28,26 3,26" fill={FILLS.butter} stroke={INK} strokeWidth="1" />
      <polygon points="3,8 13,8 16,11 28,11 28,13 3,13" fill={FILLS.butterSoft} />
      <rect x="3" y="13" width="25" height="1" fill={FILLS.butterDeep} />
      <rect x="6" y="17" width="12" height="1" fill={FILLS.butterDeep} />
      <rect x="6" y="20" width="8" height="1" fill={FILLS.butterDeep} />
    </g>
  ),

  sheet: (
    <g>
      <rect x="6" y="4" width="21" height="26" fill={INK} />
      <rect x="5" y="3" width="21" height="26" fill={WHITE} stroke={INK} strokeWidth="1" />
      <rect x="5" y="3" width="21" height="5" fill={FILLS.aquaDeep} />
      <rect x="6" y="4" width="19" height="1" fill={FILLS.aquaMid} />
      <path d="M 5 13 h 21 M 5 18 h 21 M 5 23 h 21 M 12 8 v 21 M 19 8 v 21" stroke={FILLS.rule} strokeWidth="1" fill="none" />
      <rect x="6" y="9" width="5" height="3" fill={FILLS.aquaSoft} />
      <rect x="13" y="14" width="5" height="3" fill="#b3e6cc" />
      <rect x="20" y="19" width="5" height="3" fill={FILLS.butter} />
      <rect x="6" y="24" width="5" height="3" fill={FILLS.magentaSoft} />
    </g>
  ),

  note: (
    <g>
      <rect x="7" y="5" width="19" height="25" fill={INK} />
      <rect x="6" y="4" width="19" height="25" fill={WHITE} stroke={INK} strokeWidth="1" />
      <rect x="6" y="4" width="19" height="4" fill={FILLS.paperDeep} stroke={INK} strokeWidth="1" />
      <rect x="9" y="2" width="2" height="7" fill={FILLS.shade} stroke={INK} strokeWidth="1" />
      <rect x="15" y="2" width="2" height="7" fill={FILLS.shade} stroke={INK} strokeWidth="1" />
      <rect x="21" y="2" width="2" height="7" fill={FILLS.shade} stroke={INK} strokeWidth="1" />
      <rect x="9" y="13" width="13" height="1" fill={FILLS.slate} />
      <rect x="9" y="16" width="13" height="1" fill={FILLS.slate} />
      <rect x="9" y="19" width="10" height="1" fill={FILLS.slate} />
      <rect x="9" y="22" width="7" height="2" fill={FILLS.magenta} />
    </g>
  ),

  donate: (
    <g>
      <rect x="4" y="12" width="25" height="17" fill={INK} />
      <rect x="3" y="11" width="25" height="17" fill={FILLS.paper} stroke={INK} strokeWidth="1" />
      <polygon points="3,11 28,11 15.5,21" fill={FILLS.paperDeep} stroke={INK} strokeWidth="1" />
      <path d="M 16 10 C 16 6 12 4 10 6 C 8 8 10 12 16 17 C 22 12 24 8 22 6 C 20 4 16 6 16 10 Z" fill={FILLS.magenta} stroke={INK} strokeWidth="1" />
      <rect x="12" y="7" width="2" height="2" fill={FILLS.magentaSoft} />
    </g>
  ),

  /* BadgeIcon welcome_neighborhood, re-seated on the 32 grid */
  chapters: (
    <g>
      <g transform="translate(3,3.5) scale(1.25)">
        <polygon points="2,12 6,8 10,12 10,21 2,21" fill={INK} />
        <polygon points="8,10 12,5 16,10 16,21 8,21" fill={INK} />
        <polygon points="14,12 18,8 22,12 22,21 14,21" fill={INK} />
      </g>
      <g transform="translate(1.5,2) scale(1.25)">
        <polygon points="2,12 6,8 10,12 10,21 2,21" fill={FILLS.aqua} stroke={INK} strokeWidth="1.25" strokeLinejoin="miter" />
        <polygon points="8,10 12,5 16,10 16,21 8,21" fill={FILLS.magenta} stroke={INK} strokeWidth="1.25" strokeLinejoin="miter" />
        <polygon points="14,12 18,8 22,12 22,21 14,21" fill={FILLS.tangerine} stroke={INK} strokeWidth="1.25" strokeLinejoin="miter" />
        <rect x="11" y="14" width="2" height="7" fill={INK} />
        <rect x="3" y="13" width="5" height="1" fill={FILLS.aquaSoft} />
        <rect x="9" y="11" width="6" height="1" fill={FILLS.magentaSoft} />
        <rect x="15" y="13" width="5" height="1" fill={FILLS.tangerineSoft} />
      </g>
    </g>
  ),

  /* BadgeIcon TrophyCup, re-seated on the 32 grid */
  trophy: (
    <g>
      <g transform="translate(3,3.5) scale(1.25)">
        <path d="M 5 4 L 19 4 L 18 13 Q 18 16 12 16 Q 6 16 6 13 Z" fill={INK} />
        <rect x="10" y="16" width="4" height="3" fill={INK} />
        <rect x="7" y="19" width="10" height="2" fill={INK} />
      </g>
      <g transform="translate(1.5,2) scale(1.25)">
        <path d="M 5 4 L 19 4 L 18 13 Q 18 16 12 16 Q 6 16 6 13 Z" fill={FILLS.tangerine} stroke={INK} strokeWidth="1.5" strokeLinejoin="miter" />
        <path d="M 5 6 Q 2 6 2 9 Q 2 11 5 11" fill="none" stroke={INK} strokeWidth="1.5" />
        <path d="M 19 6 Q 22 6 22 9 Q 22 11 19 11" fill="none" stroke={INK} strokeWidth="1.5" />
        <rect x="7" y="5" width="2" height="8" fill={FILLS.tangerineSoft} />
        <rect x="10" y="16" width="4" height="3" fill="#c46b22" stroke={INK} strokeWidth="1.25" />
        <rect x="7" y="19" width="10" height="2" fill="#c46b22" stroke={INK} strokeWidth="1.25" />
      </g>
    </g>
  ),

  computer: (
    <g>
      <rect x="5" y="5" width="23" height="18" fill={INK} />
      <rect x="4" y="4" width="23" height="18" fill={FILLS.silverFace} stroke={INK} strokeWidth="1" />
      <rect x="4" y="4" width="21" height="1" fill={WHITE} />
      <rect x="7" y="7" width="17" height="11" fill={FILLS.aquaDeep} stroke={INK} strokeWidth="1" />
      <rect x="8" y="8" width="15" height="4" fill={FILLS.aqua} />
      <rect x="9" y="14" width="7" height="1" fill={FILLS.greenLcd} />
      <rect x="9" y="16" width="10" height="1" fill={FILLS.greenLcd} />
      <rect x="13" y="22" width="6" height="3" fill={FILLS.silver} stroke={INK} strokeWidth="1" />
      <rect x="6" y="25" width="20" height="4" fill={FILLS.silver} stroke={INK} strokeWidth="1" />
      <rect x="8" y="26" width="16" height="1" fill={WHITE} />
      <rect x="22" y="19" width="2" height="2" fill={FILLS.magenta} />
    </g>
  ),
};

/* ── 24-grid review marks (ReviewRatingIcon shapes, unchanged) ─────────── */

const GLYPHS_24 = {
  favorite: (
    <g>
      <polygon points="12,3 14.4,9.5 21,10 16,14.5 17.5,21 12,17.5 6.5,21 8,14.5 3,10 9.6,9.5" fill={FILLS.butter} stroke={INK} strokeWidth="1.5" strokeLinejoin="miter" />
      <polygon points="12,7 13.5,11 17,11.3 14.5,13.5 12,12 9.5,13.5 7,11.3 10.5,11" fill={FILLS.tangerine} />
    </g>
  ),
  consider: (
    <g>
      <path d="M 7 10 Q 7 4 12 4 Q 17 4 17 10 Q 17 13 14.5 14.5 L 14.5 16 L 9.5 16 L 9.5 14.5 Q 7 13 7 10 Z" fill={FILLS.aqua} stroke={INK} strokeWidth="1.5" strokeLinejoin="miter" />
      <path d="M 10 8 Q 10 6 12 6" fill="none" stroke={WHITE} strokeWidth="1.25" strokeLinecap="square" />
      <rect x="9.5" y="16.5" width="5" height="2" fill="#1a8995" stroke={INK} strokeWidth="1.25" />
      <rect x="10" y="18.5" width="4" height="2" fill="#1a8995" stroke={INK} strokeWidth="1.25" />
      <line x1="11" y1="20.5" x2="13" y2="20.5" stroke={INK} strokeWidth="1.25" />
    </g>
  ),
  pass: (
    <g>
      <polygon points="3,8 11,8 11,5 19,12 11,19 11,16 3,16" fill={FILLS.grape} stroke={INK} strokeWidth="1.5" strokeLinejoin="miter" />
      <polygon points="5,10 9.5,10 9.5,8 15,12 9.5,16 9.5,14 5,14" fill="#4a3592" />
    </g>
  ),
  ineligible: (
    <g>
      <circle cx="12" cy="12" r="9" fill={FILLS.rose} stroke={INK} strokeWidth="1.5" />
      <circle cx="12" cy="12" r="6" fill={FILLS.paper} stroke={INK} strokeWidth="1" />
      <rect x="5.5" y="10.5" width="13" height="3" fill={FILLS.rose} stroke={INK} strokeWidth="1.5" transform="rotate(-45 12 12)" />
    </g>
  ),
  norating: (
    <g>
      <circle cx="12" cy="12" r="9" fill="#c8c8d4" stroke={INK} strokeWidth="1.5" />
      <circle cx="9" cy="11" r="1.25" fill={INK} />
      <circle cx="15" cy="11" r="1.25" fill={INK} />
      <path d="M 8.5 16 Q 12 14 15.5 16" fill="none" stroke={INK} strokeWidth="1.5" strokeLinecap="square" />
    </g>
  ),

  /* Shortlist — a clipboard with a starred pick */
  shortlist: (
    <g>
      <rect x="4" y="3" width="14" height="18" fill={WHITE} stroke={INK} strokeWidth="1.5" />
      <rect x="8" y="1.5" width="6" height="4" fill={FILLS.silverFace} stroke={INK} strokeWidth="1.25" />
      <rect x="6.5" y="8" width="9" height="1.5" fill={FILLS.slate} />
      <rect x="6.5" y="11.5" width="9" height="1.5" fill={FILLS.slate} />
      <rect x="6.5" y="15" width="6" height="1.5" fill={FILLS.slate} />
      <polygon points="17.5,12 19.2,15.4 23,15.9 20.2,18.5 20.9,22.2 17.5,20.4 14.1,22.2 14.8,18.5 12,15.9 15.8,15.4" fill={FILLS.butter} stroke={INK} strokeWidth="1.25" strokeLinejoin="miter" />
    </g>
  ),

  /* Comment bubble — pairs with a count in list rows */
  comment: (
    <g>
      <rect x="3" y="4" width="18" height="13" fill={FILLS.aqua} stroke={INK} strokeWidth="1.5" />
      <rect x="4.5" y="5.5" width="15" height="1.5" fill={FILLS.aquaSoft} />
      <polygon points="7,17 6,22 12,17" fill={FILLS.aqua} stroke={INK} strokeWidth="1.5" strokeLinejoin="miter" />
      <rect x="6.5" y="8.5" width="11" height="1.5" fill={WHITE} />
      <rect x="6.5" y="12" width="8" height="1.5" fill={WHITE} />
    </g>
  ),

  /* ── Pitch category marks — one per PITCH_CATEGORIES bucket ──────────── */
  catFood: (
    <g>
      <rect x="4" y="8" width="13" height="11" fill={FILLS.tangerine} stroke={INK} strokeWidth="1.5" />
      <rect x="5.5" y="9.5" width="2" height="8" fill={FILLS.tangerineSoft} />
      <path d="M 17 10 Q 21 10 21 13 Q 21 16 17 16" fill="none" stroke={INK} strokeWidth="1.5" />
      <path d="M 7.5 2.5 Q 6.5 4 7.5 5.5 M 11 2.5 Q 10 4 11 5.5 M 14.5 2.5 Q 13.5 4 14.5 5.5" fill="none" stroke={FILLS.shade} strokeWidth="1.25" strokeLinecap="square" />
      <rect x="3" y="19" width="16" height="2" fill={FILLS.paperDeep} stroke={INK} strokeWidth="1.25" />
    </g>
  ),
  catProducts: (
    <g>
      <polygon points="4,8 12,4 20,8 12,12" fill={FILLS.butterSoft} stroke={INK} strokeWidth="1.5" strokeLinejoin="miter" />
      <polygon points="4,8 12,12 12,21 4,17" fill={FILLS.butter} stroke={INK} strokeWidth="1.5" strokeLinejoin="miter" />
      <polygon points="20,8 12,12 12,21 20,17" fill={FILLS.butterDeep} stroke={INK} strokeWidth="1.5" strokeLinejoin="miter" />
      <polygon points="8,6 16,10 16,13 8,9" fill={FILLS.magenta} stroke={INK} strokeWidth="1" />
    </g>
  ),
  catWellness: (
    <g>
      <path d="M 12 21 C 4 16 3 8 7 5 C 10 3 12 6 12 9 C 12 6 14 3 17 5 C 21 8 20 16 12 21 Z" fill={FILLS.greenMid} stroke={INK} strokeWidth="1.5" strokeLinejoin="miter" />
      <path d="M 12 9 L 12 17" stroke={FILLS.greenDark} strokeWidth="1.25" />
      <path d="M 12 12 L 9 10 M 12 15 L 15 12.5" stroke={FILLS.greenDark} strokeWidth="1.25" strokeLinecap="square" />
    </g>
  ),
  catArts: (
    <g>
      <path d="M 12 3 C 6 3 3 7.5 3 12 C 3 16.5 6 21 12 21 C 14 21 14.5 19.5 13.5 18 C 12.5 16.5 13.5 15 15.5 15 L 18 15 C 20 15 21 13.5 21 11.5 C 21 6.5 17 3 12 3 Z" fill={FILLS.paper} stroke={INK} strokeWidth="1.5" strokeLinejoin="miter" />
      <rect x="6.5" y="7" width="3" height="3" fill={FILLS.magenta} stroke={INK} strokeWidth="1" />
      <rect x="12" y="5.5" width="3" height="3" fill={FILLS.aqua} stroke={INK} strokeWidth="1" />
      <rect x="16" y="9.5" width="3" height="3" fill={FILLS.butter} stroke={INK} strokeWidth="1" />
      <rect x="6.5" y="13" width="3" height="3" fill={FILLS.grape} stroke={INK} strokeWidth="1" />
    </g>
  ),
  catEducation: (
    <g>
      <polygon points="12,4 22,9 12,14 2,9" fill={FILLS.grape} stroke={INK} strokeWidth="1.5" strokeLinejoin="miter" />
      <polygon points="12,4 22,9 12,10.5 2,9" fill={FILLS.grapeMid} />
      <path d="M 6.5 11.5 V 17 C 6.5 19 17.5 19 17.5 17 V 11.5" fill={FILLS.grapeDeep} stroke={INK} strokeWidth="1.5" />
      <path d="M 21 9.5 V 15" stroke={INK} strokeWidth="1.5" strokeLinecap="square" />
      <rect x="20" y="15" width="2" height="3" fill={FILLS.butter} stroke={INK} strokeWidth="1" />
    </g>
  ),
  catServices: (
    <g>
      <path d="M 15 3.5 C 12 4 10.5 7 11.5 9.5 L 4 17 L 7 20 L 14.5 12.5 C 17 13.5 20 12 20.5 9 L 17.5 10.5 L 13.5 6.5 Z" fill={FILLS.silverFace} stroke={INK} strokeWidth="1.5" strokeLinejoin="miter" />
      <path d="M 5.5 17 L 7 18.5" stroke={FILLS.shade} strokeWidth="1.25" strokeLinecap="square" />
      <rect x="15" y="15" width="5" height="5" fill={FILLS.aqua} stroke={INK} strokeWidth="1.25" />
      <rect x="16.5" y="16.5" width="2" height="2" fill={WHITE} />
    </g>
  ),
  catTech: (
    <g>
      <rect x="6" y="6" width="12" height="12" fill={FILLS.aqua} stroke={INK} strokeWidth="1.5" />
      <rect x="7.5" y="7.5" width="9" height="2" fill={FILLS.aquaMid} />
      <rect x="9.5" y="9.5" width="5" height="5" fill={FILLS.aquaDeep} stroke={INK} strokeWidth="1" />
      <path d="M 8.5 6 V 3 M 12 6 V 3 M 15.5 6 V 3 M 8.5 21 V 18 M 12 21 V 18 M 15.5 21 V 18 M 6 8.5 H 3 M 6 12 H 3 M 6 15.5 H 3 M 21 8.5 H 18 M 21 12 H 18 M 21 15.5 H 18" stroke={INK} strokeWidth="1.25" strokeLinecap="square" />
    </g>
  ),
  catCivic: (
    <g>
      <path d="M 5 3 V 21" stroke={INK} strokeWidth="1.5" strokeLinecap="square" />
      <polygon points="5,4 19,7 5,10" fill={FILLS.magenta} stroke={INK} strokeWidth="1.5" strokeLinejoin="miter" />
      <rect x="6.5" y="5.4" width="8" height="1.2" fill="#f6b3cd" />
      <path d="M 12 13 C 12 11.5 10 10.5 9 11.5 C 8 12.5 9 14 12 16.5 C 15 14 16 12.5 15 11.5 C 14 10.5 12 11.5 12 13 Z" fill={FILLS.magenta} stroke={INK} strokeWidth="1.25" transform="translate(3, 4)" />
    </g>
  ),
};

/* Pitch category → icon kind. Falls back to "note" for unknown buckets. */
export const CATEGORY_ICONS = {
  "Food & Drink": "catFood",
  "Products": "catProducts",
  "Wellness": "catWellness",
  "Arts & Media": "catArts",
  "Education": "catEducation",
  "Services": "catServices",
  "Tech": "catTech",
  "Civic & Impact": "catCivic",
};

export const OS_ICON_KINDS = [
  ...Object.keys(GLYPHS_32),
  ...Object.keys(GLYPHS_24),
];

/**
 * @param {string} kind  one of OS_ICON_KINDS
 * @param {number} size  rendered px — use 32 on the desktop, 24 in window
 *                       chrome and list rows, 16 in the taskbar
 */
export default function OSIcon32({ kind, size = 32, className, style }) {
  const glyph32 = GLYPHS_32[kind];
  const glyph24 = GLYPHS_24[kind];
  if (!glyph32 && !glyph24) return null;

  return (
    <svg
      width={size}
      height={size}
      viewBox={glyph32 ? "0 0 32 32" : "0 0 24 24"}
      shapeRendering={glyph32 ? "crispEdges" : undefined}
      className={className}
      style={{ display: "block", ...style }}
      aria-hidden="true"
      focusable="false"
    >
      {glyph32 || glyph24}
    </svg>
  );
}
